export type PaymentCheckoutFlowLabels = {
  card: string;
  cardHolderName: string;
  cardNumber: string;
  expiryDate: string;
  securityCode: string;
  securityCodePlaceholder: string;
  expiryMonthPlaceholder: string;
  expiryYearPlaceholder: string;
};

export const defaultPaymentCheckoutFlowLabels: PaymentCheckoutFlowLabels = {
  card: 'Card',
  cardHolderName: 'Cardholder name',
  cardNumber: 'Card number',
  expiryDate: 'Expiry date',
  securityCode: 'Security code',
  securityCodePlaceholder: 'CVV',
  expiryMonthPlaceholder: 'MM',
  expiryYearPlaceholder: 'YY',
};

// i18n tags that differ from Checkout / Adyen locale strings; others pass through normalized BCP-47.
const paymentSdkLocaleOverrides = {
  en: 'en-US',
  'en-US': 'en-US',
  'en-GB': 'en',
} satisfies Record<string, string>;

const normalizeI18nLanguageTag = (i18nLanguage: string): string => {
  const trimmed = i18nLanguage.trim();
  if (trimmed === '') {
    return 'en-US';
  }

  const segments = trimmed.split('-');
  const language = segments[0]?.toLowerCase();
  if (language == null || language === '') {
    return 'en-US';
  }

  const region = segments[1];
  if (region == null || region === '') {
    return language;
  }

  return `${language}-${region.toUpperCase()}`;
};

/** Maps app i18n language to Checkout / Adyen Web Components `locale`. */
const resolvePaymentSdkLocaleOverride = (normalized: string): string | undefined => {
  for (const [tag, sdkLocale] of Object.entries(paymentSdkLocaleOverrides)) {
    if (tag === normalized) {
      return sdkLocale;
    }
  }

  return undefined;
};

export const paymentCardSessionViewerLocale = (i18nLanguage: string): string => {
  const normalized = normalizeI18nLanguageTag(i18nLanguage);
  const override = resolvePaymentSdkLocaleOverride(normalized);

  return override ?? normalized;
};

/** Checkout `translations` map key (e.g. `en` for `en-US`). */
export const paymentCheckoutTranslationsLanguageKey = (locale: string): string => {
  const [language] = locale.split('-');

  return language?.toLowerCase() ?? 'en';
};

type PaymentCheckoutPayButtonLabels = {
  saveCard: string;
  saving: string;
  saved: string;
};

export const buildPaymentCheckoutCustomTranslations = (
  flow: PaymentCheckoutFlowLabels,
  payButtons: PaymentCheckoutPayButtonLabels,
) => ({
  card: flow.card,
  'card.card_holder_name': flow.cardHolderName,
  'card.card_number': flow.cardNumber,
  'card.expiry_date': flow.expiryDate,
  'card.security_code': flow.securityCode,
  'card.security_code.placeholder': flow.securityCodePlaceholder,
  'card.expiry_date.placeholder.month': flow.expiryMonthPlaceholder,
  'card.expiry_date.placeholder.year': flow.expiryYearPlaceholder,
  'pay_button.confirm': payButtons.saveCard,
  'pay_button.confirming': payButtons.saving,
  'pay_button.pay': payButtons.saveCard,
  'pay_button.payment_processing': payButtons.saving,
  'pay_button.payment_complete': payButtons.saved,
});

export type PaymentCardSessionI18nInput = {
  viewerLocale: string;
  checkoutFlow: PaymentCheckoutFlowLabels;
  saveCard: string;
  saving: string;
  saved: string;
};

export type PaymentCardSessionI18n = {
  locale: string;
  translationLanguage: string;
  fieldTranslations: ReturnType<typeof buildPaymentCheckoutCustomTranslations>;
};

export const paymentCardSessionI18nSignature = (input: PaymentCardSessionI18nInput): string =>
  JSON.stringify(input);

export const buildPaymentCardSessionI18n = (
  input: PaymentCardSessionI18nInput,
): PaymentCardSessionI18n => {
  const translationLanguage = paymentCheckoutTranslationsLanguageKey(input.viewerLocale);

  return {
    locale: input.viewerLocale,
    translationLanguage,
    fieldTranslations: buildPaymentCheckoutCustomTranslations(input.checkoutFlow, {
      saveCard: input.saveCard,
      saving: input.saving,
      saved: input.saved,
    }),
  };
};
